"use client";
import { useMsal } from "@skanska/vsa-client-auth";
import { Button, Label, makeStyles, tokens } from "@fluentui/react-components";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CheckoutMessageBar } from "../components/CheckoutMessageBar";
import { DisplayField } from "../components/DisplayField";
import { ToolOverviewSkeleton } from "../components/skeletons/ToolOverviewSkeleton";
import ActivitiesUsingToolTable from "../components/tool/ActivitiesUsingToolTable";
import CreateToolDraftButton from "../components/tool/CreateToolDraftButton";
import { OldToolVersionMessageBar } from "../components/tool/OldToolVersionMessageBar";
import ToolDownloadButton from "../components/tool/ToolDownloadButton";
import ToolHistoryDialog from "../components/tool/ToolHistoryDialog";
import ToolLink from "../components/tool/ToolLink";
import {
  useGetDocumentFields,
  useGetLinkFields,
  useGetCurrentToolDraft,
  useGetPublishedTool,
} from "../hooks/useTools";
import { ClientError } from "../models/ClientError";
import { FieldValue } from "../models/Field";
import { FieldTypes } from "../models/FieldTypes";
import { ToolTypes } from "../models/Tool";
import { getToolType } from "../services/ToolService";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  wrapper: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxWidth: "1000px",
  },
  labels: {
    fontWeight: "bold",
  },
  fields: {
    marginTop: "16px",
    display: "grid",
    rowGap: "12px",
  },
  buttons: {
    display: "flex",
    gap: "10px",
    padding: tokens.spacingVerticalM,
  },
  toolLinkActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
  },
});

interface IToolOverviewPage {
  oldToolVersion: boolean;
}

const ToolOverviewPage = (props: IToolOverviewPage) => {
  const { oldToolVersion } = props;

  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { instance } = useMsal();
  const currentUser = instance.getActiveAccount();

  if (!id) {
    const missingIdError: ClientError = {
      message: "Tool id is missing",
      name: "Tool id is missing",
    };

    throw missingIdError;
  }

  const {
    data: tool,
    isLoading: toolLoading,
    isRefetching,
  } = useGetPublishedTool(id, oldToolVersion);
  const { data: currentDraft } = useGetCurrentToolDraft(tool?.toolId);

  const { data: linkFields, isLoading: linkFieldsLoading } = useGetLinkFields();
  const { data: documentFields, isLoading: documentFieldsLoading } =
    useGetDocumentFields();

  const isCurrentUsersDraft = useMemo(() => {
    if (!currentDraft) {
      return true;
    }

    const userEntraId = currentUser?.idTokenClaims?.oid;

    if (!userEntraId) {
      return false;
    }

    return userEntraId === currentDraft.createdBy?.id;
  }, [currentDraft, currentUser]);

  const toolType = tool ? getToolType(tool) : undefined;
  const viewDefinition =
    toolType === ToolTypes.Document ? documentFields : linkFields;
  const viewDefinitionLoading =
    toolType === ToolTypes.Document ? documentFieldsLoading : linkFieldsLoading;

  const getFieldType = (typeNum: number): FieldTypes => {
    switch (typeNum) {
      case 0:
        return FieldTypes.Tag;
      case 1:
        return FieldTypes.BooleanSwitch;
      case 2:
        return FieldTypes.BooleanCheckBox;
      case 3:
        return FieldTypes.User;
      default:
        throw new Error("Unknown field type");
    }
  };

  const renderField = (fieldName: string) => {
    if (!viewDefinition) return null;
    const meta = viewDefinition.fields.find((f) => f.uniqueName === fieldName);
    if (!meta) return null;

    const type = getFieldType(meta.type);

    let value: FieldValue;

    if (type === FieldTypes.Tag) {
      value = tool?.fieldValues?.tags[fieldName];
    } else if (
      type === FieldTypes.BooleanCheckBox ||
      type === FieldTypes.BooleanSwitch
    ) {
      value = tool?.fieldValues?.booleans[fieldName];
    } else {
      value = tool?.fieldValues?.users[fieldName];
    }

    return (
      <Fragment key={fieldName}>
        <Label className={classes.labels}>{meta.displayName}</Label>
        <DisplayField label={meta.displayName} value={value} type={type} />
      </Fragment>
    );
  };

  return (
    <div className={classes.root}>
      {tool && !tool.isCurrentVersion && tool.toolId && (
        <OldToolVersionMessageBar toolId={tool.toolId} />
      )}
      {!isCurrentUsersDraft && currentDraft?.createdBy && (
        <CheckoutMessageBar checkedOutTo={currentDraft.createdBy} />
      )}
      <div className={classes.wrapper}>
        {toolLoading || viewDefinitionLoading || isRefetching ? (
          <ToolOverviewSkeleton />
        ) : tool && tool.toolId ? (
          <div className={classes.content}>
            <h2>{tool.title}</h2>
            {tool.adminDisplayName && (
              <>
                <Label className={classes.labels}>
                  {t("adminDisplayNameLabel")}
                </Label>
                <span>{tool.adminDisplayName}</span>
              </>
            )}
            <Label className={classes.labels}>{t("tool")}</Label>
            <div className={classes.toolLinkActions}>
              <ToolLink tool={tool} />
              {toolType === ToolTypes.Document &&
                tool.fileUniqueId &&
                tool.versionId && <ToolDownloadButton tool={tool} showLabel />}
            </div>
            {tool.fileUniqueId && (
              <span>{t("documentOverviewDocumentLinkDescription")}</span>
            )}
            <div className={classes.fields}>
              {Object.keys(tool.fieldValues?.tags ?? {}).map(renderField)}
              {Object.keys(tool.fieldValues?.booleans ?? {}).map(renderField)}
              {Object.keys(tool.fieldValues?.users ?? {}).map(renderField)}
            </div>
            <Label className={classes.labels}>
              {t("activitiesUsingThisTool")}
            </Label>
            {tool.activities && tool.activities.length > 0 ? (
              <ActivitiesUsingToolTable activities={tool.activities} />
            ) : (
              <p>{t("noActivitiesUsingThisTool")}</p>
            )}
          </div>
        ) : (
          <div>{t("toolNotFound")}</div>
        )}
      </div>

      <div className={classes.buttons}>
        <Button onClick={() => navigate(-1)} appearance="secondary">
          {t("back")}
        </Button>
        {tool && tool.toolId && (
          <>
            <CreateToolDraftButton
              toolId={tool.toolId}
              versionId={tool.versionId!}
              oldVersion={oldToolVersion}
            />
            {tool.isCurrentVersion && (
              <ToolHistoryDialog toolId={tool.toolId} />
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default ToolOverviewPage;
